import { scoreTone, scoreRating, overallHeadline, PILL_LIGHT, PILL_DARK, BAR } from '../utils/resumeReport';

export default function ResumeReportCard({ analysis, isDarkMode }) {
    let results = null;
    try { results = analysis.ResultsJSON ? JSON.parse(analysis.ResultsJSON) : null; } catch { /* malformed JSON */ }

    if (!results) return null;

    const categories = results.categories || [];
    const overallScore = results.overallScore;
    const submitted = analysis.CreatedOn ? new Date(analysis.CreatedOn).toLocaleDateString() : null;

    const heading = isDarkMode ? 'text-slate-100' : 'text-slate-900';
    const sub = isDarkMode ? 'text-slate-400' : 'text-slate-600';
    const body = isDarkMode ? 'text-slate-300' : 'text-slate-700';
    const card = isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200';
    const pill = (score) => (isDarkMode ? PILL_DARK : PILL_LIGHT)[scoreTone(score)];

    const strengths = categories.filter(c => scoreTone(c.score) === 'green');
    const growth = categories.filter(c => ['yellow', 'red'].includes(scoreTone(c.score)));

    const esc = (s) => {
        if (typeof s !== 'string') return String(s ?? '');
        return s.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
    };

    const handlePrint = () => {
        const printArea = document.getElementById('resume-report-printable');
        if (!printArea) return;
        const w = window.open('', '_blank');
        const label = analysis.NickName || results.targetJob || 'Resume Analysis';
        w.document.write(`<html><head><title>${esc(label)} — Pono Pilot</title>
            <style>body{font-family:Arial,sans-serif;max-width:800px;margin:0 auto;padding:24px;color:#1e293b}
            .card{border:1px solid #e2e8f0;border-radius:12px;padding:16px;margin-bottom:16px}
            .pill{display:inline-block;padding:2px 10px;border-radius:99px;font-size:12px;font-weight:600}
            .green{background:#d1fae5;color:#065f46}.yellow{background:#fef3c7;color:#92400e}.red{background:#ffe4e6;color:#9f1239}
            .bar-bg{height:8px;border-radius:8px;background:#f1f5f9;margin:8px 0}
            .bar{height:8px;border-radius:8px}
            .bar-green{background:#10b981}.bar-yellow{background:#f59e0b}.bar-red{background:#f43f5e}
            .found{display:inline-block;padding:2px 8px;border-radius:99px;font-size:11px;background:#d1fae5;color:#065f46;margin:2px}
            .missing{display:inline-block;padding:2px 8px;border-radius:99px;font-size:11px;background:#fef3c7;color:#92400e;margin:2px}
            h1{font-size:24px;margin-bottom:4px}h2{font-size:18px;margin:12px 0 8px}h3{font-size:15px;margin:0}
            .meta{color:#64748b;font-size:13px}.rec{font-size:13px;color:#334155}
            .top-rec{background:#f0fdf4;border:1px solid #bbf7d0;border-radius:12px;padding:12px;margin-top:16px}
            .footer{margin-top:32px;padding-top:16px;border-top:1px solid #e2e8f0;text-align:center;color:#94a3b8;font-size:11px}
            @media print{body{padding:12px}}</style></head><body>`);
        w.document.write(`<h1>Resume Analysis Results</h1>`);
        w.document.write(`<p class="meta">${esc(label)} ${submitted ? '· Analyzed ' + submitted : ''}</p>`);
        if (results.targetJob) w.document.write(`<p class="meta">Target Role: <strong>${esc(results.targetJob)}</strong></p>`);
        w.document.write(`<div class="card"><div style="display:flex;align-items:center;gap:16px">`);
        w.document.write(`<div><span style="font-size:36px;font-weight:800">${Math.round(overallScore)}</span><span style="font-size:16px;color:#64748b">/100</span><br>`);
        const t = scoreTone(overallScore);
        w.document.write(`<span class="pill ${t}">${scoreRating(overallScore)}</span></div>`);
        w.document.write(`<div><strong style="font-size:16px">${esc(overallHeadline(overallScore))}</strong>`);
        if (results.summary) w.document.write(`<p class="rec" style="margin-top:4px">${esc(results.summary)}</p>`);
        w.document.write(`</div></div>`);
        if (results.resumeMatchScore != null || results.resumeReadinessScore != null) {
            w.document.write(`<div style="display:flex;gap:24px;margin-top:12px;padding-top:12px;border-top:1px solid #e2e8f0">`);
            if (results.resumeMatchScore != null) w.document.write(`<div><span class="meta">Match Score</span><br><strong>${Math.round(results.resumeMatchScore * 100)}%</strong></div>`);
            if (results.resumeReadinessScore != null) w.document.write(`<div><span class="meta">Readiness Score</span><br><strong>${esc(results.resumeReadinessScore)}</strong></div>`);
            w.document.write(`</div>`);
        }
        w.document.write(`</div>`);
        categories.forEach(c => {
            const ct = scoreTone(c.score);
            w.document.write(`<div class="card"><div style="display:flex;justify-content:space-between;align-items:center">`);
            w.document.write(`<h3>${esc(c.name)}</h3>`);
            w.document.write(`<span class="pill ${ct}">${Math.round(c.score)} · ${scoreRating(c.score)}</span></div>`);
            w.document.write(`<div class="bar-bg"><div class="bar bar-${ct}" style="width:${Math.round(c.score)}%"></div></div>`);
            if (c.found?.length) { w.document.write(`<p class="meta" style="margin:8px 0 4px;text-transform:uppercase;font-size:10px;letter-spacing:1px"><strong>Matched</strong></p>`); c.found.forEach(f => w.document.write(`<span class="found">${esc(f)}</span>`)); }
            if (c.missing?.length) { w.document.write(`<p class="meta" style="margin:8px 0 4px;text-transform:uppercase;font-size:10px;letter-spacing:1px"><strong>To Add</strong></p>`); c.missing.forEach(m => w.document.write(`<span class="missing">${esc(m)}</span>`)); }
            if (c.suggestions?.length) { w.document.write(`<p class="meta" style="margin:8px 0 4px;text-transform:uppercase;font-size:10px;letter-spacing:1px"><strong>Recommendations</strong></p><ul style="margin:0;padding-left:16px">`); c.suggestions.forEach(s => w.document.write(`<li class="rec">${esc(s)}</li>`)); w.document.write(`</ul>`); }
            w.document.write(`</div>`);
        });
        if (results.topRecommendation) w.document.write(`<div class="top-rec"><strong>Top Recommendation:</strong> ${esc(results.topRecommendation)}</div>`);
        w.document.write(`<div class="footer">Powered by Pono Pilot &middot; ponopilot.com</div>`);
        w.document.write(`</body></html>`);
        w.document.close();
        setTimeout(() => w.print(), 300);
    };

    return (
        <div className="space-y-6" id="resume-report-printable">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <button
                    onClick={handlePrint}
                    className="shrink-0 w-full sm:w-auto px-4 py-2 rounded-lg text-sm font-medium bg-primary-600 text-white hover:bg-primary-700 transition-colors text-center order-2 sm:order-1"
                >
                    Download PDF
                </button>
                <div className="min-w-0 text-left sm:text-right order-1 sm:order-2">
                    <h1 className={`text-xl sm:text-2xl md:text-3xl font-extrabold ${heading}`}>
                        Resume Analysis Results
                    </h1>
                    <p className={`mt-1 text-sm break-words ${sub}`}>
                        {analysis.FileName} {submitted ? `· Analyzed ${submitted}` : ''}
                    </p>
                    {results.targetJob && (
                        <p className={`text-sm mt-0.5 ${sub}`}>Target Role: <span className={`font-semibold ${heading}`}>{results.targetJob}</span></p>
                    )}
                </div>
            </div>

            <div className={`rounded-2xl border p-5 md:p-6 ${card}`}>
                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                    <div className="shrink-0">
                        <div className={`text-4xl font-extrabold leading-none ${heading}`}>
                            {overallScore != null ? Math.round(overallScore) : '—'}
                            <span className={`text-lg font-semibold ${sub}`}>/100</span>
                        </div>
                        <span className={`inline-block mt-2 text-xs font-semibold px-2.5 py-1 rounded-full ${pill(overallScore)}`}>
                            {scoreRating(overallScore)}
                        </span>
                    </div>
                    <div className="min-w-0">
                        <p className={`text-lg font-bold ${heading}`}>{overallHeadline(overallScore)}</p>
                        {results.summary && <p className={`text-sm mt-1 ${body}`}>{results.summary}</p>}
                    </div>
                </div>
                {(results.resumeMatchScore != null || results.resumeReadinessScore != null) && (
                    <div className={`flex gap-6 mt-4 pt-4 border-t ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                        {results.resumeMatchScore != null && (
                            <div>
                                <p className={`text-xs font-semibold uppercase tracking-wide ${sub}`}>Match Score</p>
                                <p className={`text-xl font-bold ${heading}`}>{Math.round(results.resumeMatchScore * 100)}%</p>
                            </div>
                        )}
                        {results.resumeReadinessScore != null && (
                            <div>
                                <p className={`text-xs font-semibold uppercase tracking-wide ${sub}`}>Readiness Score</p>
                                <p className={`text-xl font-bold ${heading}`}>{results.resumeReadinessScore}</p>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {strengths.length > 0 && (
                <section className={`rounded-2xl border p-5 ${card}`}>
                    <h2 className={`text-lg font-bold mb-3 ${heading}`}>Where you stand out</h2>
                    <div className="space-y-3">
                        {strengths.map((c, i) => (
                            <div key={i}>
                                <p className={`font-semibold ${heading}`}>{c.name}</p>
                                {c.suggestions?.length > 0 && (
                                    <p className={`text-sm ${body}`}>{c.suggestions[0]}</p>
                                )}
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {growth.length > 0 && (
                <section className={`rounded-2xl border p-5 ${card}`}>
                    <h2 className={`text-lg font-bold mb-3 ${heading}`}>Where to focus next</h2>
                    <div className="space-y-3">
                        {growth.map((c, i) => (
                            <div key={i}>
                                <p className={`font-semibold ${heading}`}>{c.name}</p>
                                {c.suggestions?.length > 0 && (
                                    <p className={`text-sm ${body}`}>{c.suggestions[0]}</p>
                                )}
                            </div>
                        ))}
                    </div>
                </section>
            )}

            <section>
                <h2 className={`text-lg font-bold mb-3 ${heading}`}>Category breakdown</h2>
                <div className="space-y-3">
                    {categories.map((c, i) => {
                        const t = scoreTone(c.score);
                        const pct = c.score != null ? Math.round(c.score) : null;
                        return (
                            <div key={i} className={`rounded-2xl border p-4 ${card}`}>
                                <div className="flex items-center justify-between gap-3 mb-2">
                                    <h3 className={`font-bold ${heading}`}>
                                        {c.name}
                                    </h3>
                                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full shrink-0 ${pill(c.score)}`}>
                                        {pct != null ? `${pct} · ` : ''}{scoreRating(c.score)}
                                    </span>
                                </div>
                                {pct != null && (
                                    <div className={`h-2 rounded-full overflow-hidden mb-3 ${isDarkMode ? 'bg-slate-700' : 'bg-slate-100'}`}>
                                        <div className={`h-full rounded-full ${BAR[t]}`} style={{ width: `${pct}%` }} />
                                    </div>
                                )}
                                {c.found?.length > 0 && (
                                    <div className="mb-2">
                                        <p className={`text-xs font-semibold uppercase tracking-wide mb-1 ${sub}`}>Skills matched</p>
                                        <div className="flex flex-wrap gap-1.5">
                                            {c.found.map((f, fi) => (
                                                <span key={fi} className={`text-xs px-2 py-0.5 rounded-full ${isDarkMode ? 'bg-primary-900/40 text-primary-300' : 'bg-primary-100 text-primary-800'}`}>{f}</span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {c.missing?.length > 0 && (
                                    <div className="mb-2">
                                        <p className={`text-xs font-semibold uppercase tracking-wide mb-1 ${sub}`}>Skills to add</p>
                                        <div className="flex flex-wrap gap-1.5">
                                            {c.missing.map((m, mi) => (
                                                <span key={mi} className={`text-xs px-2 py-0.5 rounded-full ${isDarkMode ? 'bg-amber-900/40 text-amber-300' : 'bg-amber-100 text-amber-800'}`}>{m}</span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {c.suggestions?.length > 0 && (
                                    <div>
                                        <p className={`text-xs font-semibold uppercase tracking-wide mb-1 ${sub}`}>Recommendations</p>
                                        <ul className={`text-sm space-y-0.5 ${body}`}>
                                            {c.suggestions.map((s, si) => <li key={si}>{'▸'} {s}</li>)}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </section>

            {results.topRecommendation && (
                <section className={`rounded-2xl border p-5 ${isDarkMode ? 'bg-primary-900/20 border-primary-800' : 'bg-primary-50 border-primary-200'}`}>
                    <h2 className={`text-lg font-bold mb-2 ${heading}`}>Top Recommendation</h2>
                    <p className={`text-sm ${body}`}>{results.topRecommendation}</p>
                </section>
            )}
        </div>
    );
}
